import { useState } from "react";
import { api, ApiError } from "../../api/client";
import type { TraceDocument } from "../../api/types";
import { S } from "../../styles";

/**
 * The supplier's own document, behind a quote that was read off one.
 *
 * A quote that arrived as a PDF — emailed in reply to the RFQ, or posted to
 * `/inbound/quotes/pdf` — was extracted, and everything on the card above is
 * that extraction. Price, lead time and condition are a model's reading of a
 * scan. Awarding on those numbers without any route back to the paper is the
 * same gap the receiving screen had when it showed a verdict and no
 * certificate.
 *
 * Three cases:
 *
 *   a document   link it, whatever channel it came in on
 *   email, none  the supplier typed the numbers into the reply instead of
 *                attaching anything. Said out loud, because silence there was
 *                indistinguishable from a broken feature — a buyer cannot tell
 *                an empty reply from a link that failed to load, and the first
 *                person to hit it reasonably reported it as a bug
 *   anything else, none   nothing rendered
 *
 * Email is the only channel where that middle case is real, and the only one
 * that gets the line. A PDF quote *is* its document: the ingest endpoint stores
 * it with the quote id, so an absence there is a storage failure or a card
 * rendered before the document query caught up — never "no attachment was
 * sent", which is what this used to claim, on a card whose quote had been read
 * out of the very PDF it said had not been sent.
 *
 * Opened through `openTraceDocument` rather than as a link, for the reason
 * stated there: the download is authenticated and a link navigation carries no
 * bearer token.
 */

export function SourceDocuments({
  documents,
  sourceChannel,
}: {
  documents: TraceDocument[];
  sourceChannel?: string | null;
}) {
  const [err, setErr] = useState<string | null>(null);

  if (documents.length === 0) {
    if (sourceChannel !== "email") return null;
    return (
      <p style={{ ...S.muted, fontSize: 11, margin: "0 0 8px" }}>
        📄 Read from the email body — no attachment was sent.
      </p>
    );
  }

  return (
    <div style={{ margin: "0 0 8px" }}>
      {documents.map((doc) => (
        <div key={doc.id}>
          <button
            type="button"
            onClick={() => {
              setErr(null);
              api.openTraceDocument(doc.id).catch((e: Error) => {
                setErr(e instanceof ApiError ? e.detail : e.message);
              });
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontSize: 12,
              color: "#1c7ed6",
              cursor: "pointer",
              textDecoration: "underline",
              textAlign: "left",
            }}
            title={doc.filename}
          >
            📄 {doc.filename} ↗
          </button>
          <span style={{ ...S.muted, fontSize: 11, marginLeft: 6 }}>
            via {doc.source_channel}
          </span>
        </div>
      ))}
      {err && (
        <p style={{ fontSize: 12, color: "#a61e1e", margin: "4px 0 0" }}>
          Could not open the document: {err}
        </p>
      )}
    </div>
  );
}
